import { useEffect, useMemo, useState } from "react";
import { ChevronDown, ChevronUp, CheckCircle2, AlertTriangle, AlertOctagon, ExternalLink, Radio, Wrench, Gauge, Check, CalendarClock } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";

// Iter 90b — the Status Bar's uptime tiles are now populated from the
// backend `/status/uptime` endpoint (see Settings → Dashboard → Pingdom
// mapping to remap the four slots).  Rendered as a 2×2 grid on mobile and
// a single 4-wide row on md+ inside the status bar dropdown.

// Iter 91z — user-selectable window for the uptime banners. Pingdom's public
// share banners accept a `?days=N` query, so switching the window re-renders
// the same image at the requested resolution. The 1-day / yesterday windows
// use the closest supported value (1) plus a cache-bust suffix so the browser
// re-fetches the banner even if Pingdom returns identical bytes.
const UPTIME_RANGES = [
  { key: "today",   label: "Today",        days: 1  },
  { key: "yday",    label: "Yesterday",    days: 1  },
  { key: "7d",      label: "Last 7 days",  days: 7  },
  { key: "30d",     label: "Last 30 days", days: 30 },
  { key: "90d",     label: "Last 90 days", days: 90 },
];
const DEFAULT_RANGE_KEY = "30d";

const LEVELS = {
  operational: {
    label: "All systems operational",
    dot: "bg-emerald-500",
    bar: "bg-emerald-50 border-emerald-200 text-emerald-800",
    Icon: CheckCircle2,
    iconColor: "text-emerald-600",
  },
  maintenance: {
    label: "Scheduled maintenance",
    dot: "bg-sky-500",
    bar: "bg-sky-50 border-sky-200 text-sky-800",
    Icon: Wrench,
    iconColor: "text-sky-600",
  },
  degraded: {
    label: "Degraded performance",
    dot: "bg-amber-500",
    bar: "bg-amber-50 border-amber-200 text-amber-900",
    Icon: AlertTriangle,
    iconColor: "text-amber-600",
  },
  // Legacy aliases used by manual settings.
  minor: {
    label: "Minor service alert",
    dot: "bg-amber-500",
    bar: "bg-amber-50 border-amber-200 text-amber-900",
    Icon: AlertTriangle,
    iconColor: "text-amber-600",
  },
  partial_outage: {
    label: "Partial outage",
    dot: "bg-orange-500",
    bar: "bg-orange-50 border-orange-200 text-orange-900",
    Icon: AlertOctagon,
    iconColor: "text-orange-600",
  },
  major_outage: {
    label: "Major outage",
    dot: "bg-red-500",
    bar: "bg-red-50 border-red-200 text-red-800",
    Icon: AlertOctagon,
    iconColor: "text-red-600",
  },
  major: {
    label: "Major service incident",
    dot: "bg-red-500",
    bar: "bg-red-50 border-red-200 text-red-800",
    Icon: AlertOctagon,
    iconColor: "text-red-600",
  },
};

// Iter 120 — Daily maintenance window (00:00–02:00 America/Edmonton, i.e.
// midnight–2am MDT). During it the strip turns bright yellow with black text
// and says so, unless a real incident is in progress — an actual outage always
// outranks the routine window.
export const MAINT_WINDOW = {
  label: "Maintenance MAY BE underway — this is a recurring daily window, so we may or may not be performing any actions right now.",
  dot: "bg-black",
  bar: "bg-yellow-300 border-yellow-500 text-black",
  Icon: Wrench,
  iconColor: "text-black",
};
const MAINT_TZ = "America/Edmonton";
const MAINT_OVERRIDABLE = new Set(["operational", "maintenance"]);

export function inMaintenanceWindow(info, now = new Date()) {
  if (!info || info.maint_window_enabled === false) return false;
  const start = Number(info.maint_window_start_hour ?? 0);
  const end = Number(info.maint_window_end_hour ?? 2);
  const hour = Number(new Intl.DateTimeFormat("en-US", {
    timeZone: MAINT_TZ, hour: "numeric", hourCycle: "h23",
  }).format(now));
  // A window that wraps midnight (e.g. 22 → 2) is inclusive of both legs.
  return start <= end ? hour >= start && hour < end : hour >= start || hour < end;
}

export const fmtHour = (h) => {
  const n = ((Number(h) % 24) + 24) % 24;
  const suffix = n < 12 ? "AM" : "PM";
  const twelve = n % 12 === 0 ? 12 : n % 12;
  return `${twelve}:00 ${suffix}`;
};

// Minutes left in the current window (null when outside it).
export function minutesToWindowEnd(info, now = new Date()) {
  if (!inMaintenanceWindow(info, now)) return null;
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: MAINT_TZ, hour: "numeric", minute: "numeric", hourCycle: "h23",
  }).formatToParts(now);
  const hour = Number(parts.find((p) => p.type === "hour").value);
  const minute = Number(parts.find((p) => p.type === "minute").value);
  const end = Number(info?.maint_window_end_hour ?? 2) * 60;
  return (end - (hour * 60 + minute) + 1440) % 1440 || 1440;
}

export const fmtLeft = (mins) => {
  if (mins == null) return "";
  if (mins < 60) return `ends in ${mins} min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m ? `ends in ${h}h ${m}m` : `ends in ${h}h`;
};

export default function StatusBar() {
  const [info, setInfo] = useState(null);
  // Iter 114y — two separate grants:
  //   • `top_bar.view`    — the top bar strip itself ("All systems operational")
  //   • `status_bar.view` — the expandable details (uptime + incident history)
  const { has } = useAuth();
  const canView = has("top_bar.view");
  const canExpand = has("status_bar.view");
  const [open, setOpen] = useState(() => localStorage.getItem("statusbar_open") === "1");
  const [rangeKey, setRangeKey] = useState(() => localStorage.getItem("statusbar_uptime_range") || DEFAULT_RANGE_KEY);
  const [uptime, setUptime] = useState({ banners: [], loading: false, source: null });
  const [maintNow, setMaintNow] = useState(false);
  const [minsLeft, setMinsLeft] = useState(null);

  // Re-evaluate the window every 30s so the strip flips on/off on its own,
  // and keep the "ends in …" countdown fresh.
  useEffect(() => {
    const tick = () => {
      setMaintNow(inMaintenanceWindow(info));
      setMinsLeft(minutesToWindowEnd(info));
    };
    tick();
    const id = setInterval(tick, 30_000);
    return () => clearInterval(id);
  }, [info]);

  // Iter 120 — Incident toast: poll the incident.io widget feed and announce a
  // newly opened incident right away instead of waiting for a page refresh.
  // Seen ids live in localStorage so one incident isn't announced twice.
  useEffect(() => {
    if (!canView) return;
    let cancelled = false;
    const seenKey = "statusbar_seen_incidents";
    const poll = async () => {
      try {
        const { data } = await api.get("/status/incident-io-widget");
        if (cancelled) return;
        const live = [...(data.ongoing_incidents || []), ...(data.in_progress_maintenances || [])];
        const seen = new Set(JSON.parse(localStorage.getItem(seenKey) || "[]"));
        const fresh = live.filter((i) => i.id && !seen.has(i.id));
        if (fresh.length) {
          fresh.forEach((inc) => {
            const maint = (inc.status || "").toLowerCase().includes("maint")
              || (data.in_progress_maintenances || []).some((m) => m.id === inc.id);
            toast[maint ? "message" : "error"](
              inc.name || (maint ? "Maintenance in progress" : "New incident"),
              {
                description: [inc.status, inc.impact].filter(Boolean).join(" · ") || "Open on the status page for details",
                duration: 15_000,
                action: info?.status_url
                  ? { label: "Status page", onClick: () => window.open(info.status_url, "_blank", "noopener") }
                  : undefined,
              },
            );
          });
        }
        // Remember everything currently live (and forget anything resolved).
        localStorage.setItem(seenKey, JSON.stringify(live.map((i) => i.id).filter(Boolean)));
      } catch { /* status feed is best-effort */ }
    };
    poll();
    const id = setInterval(poll, 60_000);
    return () => { cancelled = true; clearInterval(id); };
  }, [canView, info?.status_url]);

  const range = useMemo(
    () => UPTIME_RANGES.find((r) => r.key === rangeKey) || UPTIME_RANGES[3],
    [rangeKey],
  );

  useEffect(() => {
    if (!canView) return;
    const load = () => api.get("/status").then((r) => setInfo(r.data)).catch(() => {});
    load();
    // Auto-refresh the banner from the live Incident.io status page every 60s.
    const id = setInterval(load, 60_000);
    return () => clearInterval(id);
  }, [canView]);

  // Iter 91ab — pull live Pingdom uptime for the selected range and refresh
  // every 60s. Backend caches per range so this stays cheap.
  useEffect(() => {
    if (!canView || !canExpand) return;
    let cancelled = false;
    const load = () => {
      setUptime((u) => ({ ...u, loading: true }));
      api.get("/status/uptime", { params: { range: rangeKey } })
        .then(({ data }) => {
          if (!cancelled) setUptime({ banners: data.banners || [], loading: false, source: data.source });
        })
        .catch(() => { if (!cancelled) setUptime({ banners: [], loading: false, source: "error" }); });
    };
    load();
    const id = setInterval(load, 60_000);
    return () => { cancelled = true; clearInterval(id); };
  }, [rangeKey, canView, canExpand]);

  // Cache-bust the banner images whenever the range changes so Pingdom
  // re-renders. Rotates once per minute so the banner stays fresh.
  const bannerBust = useMemo(
    () => `${rangeKey}-${Math.floor(Date.now() / 60_000)}`,
    [rangeKey],
  );
  const bannerUrl = (u) => `${u}?days=${range.days}&_=${bannerBust}`;

  const pickRange = (k) => {
    setRangeKey(k);
    localStorage.setItem("statusbar_uptime_range", k);
  };

  if (!canView) return null;
  if (!info) return null;
  const baseCfg = LEVELS[info.status_level] || LEVELS.operational;
  const maintenanceWindow = maintNow && MAINT_OVERRIDABLE.has(info.status_level || "operational");
  const cfg = maintenanceWindow ? MAINT_WINDOW : baseCfg;
  // Iter 122 — during the bright-yellow maintenance window the inner panels
  // must stay light with dark text; the dark-mode surfaces underneath turned
  // black-on-olive and were unreadable.
  const panelCls = maintenanceWindow
    ? "rounded-lg border border-yellow-700/40 bg-white p-3 text-slate-900 shadow-sm"
    : "rounded-lg border border-white/60 bg-white/70 dark:border-white/10 dark:bg-slate-950/40 p-3";
  const subPanelCls = maintenanceWindow
    ? "mt-2 flex items-start gap-3 rounded border border-yellow-700/30 bg-white p-3 text-slate-900"
    : "mt-2 flex items-start gap-3 rounded border border-white/60 bg-white/80 dark:border-white/10 dark:bg-slate-950/60 p-3";
  const mutedCls = maintenanceWindow ? "text-slate-600" : "opacity-70";
  const Icon = cfg.Icon;
  const isLive = info.status_source === "incident.io";
  const toggle = () => {
    if (!canExpand) return;
    const next = !open;
    setOpen(next);
    localStorage.setItem("statusbar_open", next ? "1" : "0");
  };

  return (
    <div className={`border-b ${cfg.bar}`} data-testid="status-bar"
      data-maintenance-window={maintenanceWindow ? "1" : "0"}>
      <div className="mx-auto max-w-[1400px] px-4 md:px-6">
        <button
          onClick={toggle}
          data-testid="status-bar-toggle"
          className="flex w-full items-center gap-3 py-2 text-left"
        >
          <span className="relative flex h-2.5 w-2.5 shrink-0">
            <span className={`absolute inline-flex h-full w-full rounded-full opacity-60 ${cfg.dot} ${info.status_level !== "operational" ? "animate-ping" : ""}`} />
            <span className={`relative inline-flex h-2.5 w-2.5 rounded-full ${cfg.dot}`} />
          </span>
          <Icon className={`h-4 w-4 shrink-0 ${cfg.iconColor}`} />
          <span className={`text-sm truncate ${maintenanceWindow ? "font-semibold text-black" : "font-medium"}`}
            title={maintenanceWindow ? MAINT_WINDOW.label : undefined}
            data-testid="status-bar-label">
            {maintenanceWindow ? MAINT_WINDOW.label : (info.status_message || cfg.label)}
          </span>
          {maintenanceWindow && minsLeft != null && (
            <span className="shrink-0 rounded-full bg-black/80 px-2 py-0.5 text-[11px] font-semibold text-yellow-200"
              data-testid="status-bar-window-countdown"
              title={`Window runs ${fmtHour(info.maint_window_start_hour ?? 0)}–${fmtHour(info.maint_window_end_hour ?? 2)} Mountain Time`}>
              {fmtLeft(minsLeft)}
            </span>
          )}
          {/* Iter 120 — optional "what we're doing tonight" note, set in
              Settings → Marketing → Dashboard. */}
          {maintenanceWindow && info.maint_window_note && (
            <span className="hidden shrink-0 items-center rounded-full border border-black/30 bg-black/10 px-2 py-0.5 text-[11px] font-semibold text-black md:inline-flex"
              data-testid="status-bar-maintenance-note" title={info.maint_window_note}>
              Tonight: {info.maint_window_note}
            </span>
          )}
          {isLive && !maintenanceWindow && (
            <span
              data-testid="status-bar-live-badge"
              title="Auto-synced from Incident.io status page"
              className="hidden sm:inline-flex items-center gap-1 rounded-full border border-white/50 bg-white/60 dark:border-white/15 dark:bg-slate-950/40 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide"
            >
              <Radio className="h-3 w-3" /> Live
            </span>
          )}
          <span className="ml-auto flex items-center gap-1 text-xs opacity-80">
            {canExpand && (open ? "Hide" : "Details")}
            {canExpand && (open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />)}
          </span>
        </button>

        {open && canExpand && (
          <div className="pb-3" data-testid="status-bar-expanded">
            {/* Iter 91g — Marketing-editable maintenance callout.
                Iter 101 — light-orange palette + CalendarClock icon so the
                scheduled maintenance notice reads as "schedule reminder"
                rather than an alarm. Values come from Settings → Dashboard
                → Maintenance banner. */}
            {(info.maintenance_title || info.maintenance_body) && (
              <div className="mb-2 flex items-start gap-2 rounded-lg border border-orange-200 bg-orange-50 px-3 py-2 text-orange-900" data-testid="status-bar-maintenance">
                <CalendarClock className="mt-0.5 h-4 w-4 shrink-0 text-orange-600" aria-hidden="true" />
                <div className="min-w-0 flex-1">
                  {info.maintenance_title && (
                    <div className="text-xs font-bold uppercase tracking-wide">
                      {info.maintenance_title}
                    </div>
                  )}
                  {info.maintenance_body && (
                    <p className="mt-1 whitespace-pre-line text-[11px] leading-relaxed text-orange-800/90">
                      {info.maintenance_body}
                    </p>
                  )}
                </div>
              </div>
            )}
            <div className={panelCls}>
              <div className="flex items-center justify-between">
                <span className={`text-xs font-semibold uppercase tracking-wide ${mutedCls}`}>Current Status</span>
                {info.status_url && (
                  <a href={info.status_url} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-xs font-medium hover:underline" data-testid="status-bar-link">
                    Open status page <ExternalLink className="h-3 w-3" />
                  </a>
                )}
              </div>
              {/* Iter 90 — incident.io ships CSP `frame-ancestors 'self'` so
                  the iframe embed always renders blank. Show a live summary
                  panel built from /api/status instead; the "Open status page"
                  link above still deep-links to the public page. */}
              <div className={`${subPanelCls}`} data-testid="status-bar-summary">
                <span className={`mt-0.5 inline-flex h-3 w-3 shrink-0 rounded-full ${cfg.dot}`} />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold">
                    {info.status_message || cfg.label}
                  </div>
                  <div className={`mt-0.5 text-[11px] ${mutedCls}`}>
                    Level: <span className="font-medium">{info.status_level}</span>
                    {maintenanceWindow && (
                      <> · <span className="font-medium">
                        Daily maintenance window ({fmtHour(info.maint_window_start_hour ?? 0)}–{fmtHour(info.maint_window_end_hour ?? 2)} MT)
                      </span></>
                    )}
                    {info.status_source && (
                      <> · Source: <span className="font-medium">{isLive ? "Incident.io (live, auto-refresh 60s)" : "Manual override"}</span></>
                    )}
                  </div>
                </div>
              </div>
              {!info.status_url && (
                <p className={`mt-1.5 text-xs ${mutedCls}`}>A live status feed will appear here once the source link is configured in Settings.</p>
              )}
            </div>

            {/* Iter 90b — Uptime Stats (moved from Dashboard). Renders under
                the alert summary above so live incidents stay on top. */}
            <div className={`mt-3 ${panelCls}`} data-testid="statusbar-uptime">
              <div className={`mb-2 flex flex-wrap items-baseline gap-1.5 text-xs font-semibold uppercase tracking-wide ${mutedCls}`}>
                <Gauge className="h-3.5 w-3.5 self-center" /> Uptime Stats
                <span className="text-[9px] font-bold normal-case tracking-normal opacity-80" data-testid="uptime-stats-suffix">
                  including regularly scheduled maintenance, with failover
                </span>
                <span className="ml-auto text-[10px] font-normal normal-case opacity-60">
                  <DropdownMenu>
                    <DropdownMenuTrigger
                      data-testid="statusbar-uptime-range-trigger"
                      className="inline-flex items-center gap-1 rounded-md border border-white/60 bg-white/60 dark:border-white/15 dark:bg-slate-950/40 px-2 py-0.5 font-medium hover:bg-white/90 dark:hover:bg-slate-900/60 focus:outline-none focus:ring-1 focus:ring-primary/40"
                    >
                      {range.label} · Pingdom
                      <ChevronDown className="h-3 w-3" />
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="min-w-[140px]"
                                         data-testid="statusbar-uptime-range-menu">
                      {UPTIME_RANGES.map((r) => (
                        <DropdownMenuItem
                          key={r.key}
                          data-testid={`statusbar-uptime-range-${r.key}`}
                          onClick={(e) => { e.preventDefault(); pickRange(r.key); }}
                          className={`flex items-center justify-between gap-3 text-xs ${
                            rangeKey === r.key ? "bg-primary/10 font-semibold text-primary" : ""
                          }`}
                        >
                          {r.label}
                          {rangeKey === r.key && <Check className="h-3.5 w-3.5" />}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </span>
              </div>
              <div className="grid grid-cols-2 gap-2 md:grid-cols-4"
                   data-testid="statusbar-uptime-grid">
                {uptime.banners.map((b) => {
                  const pct = b?.uptime_pct;
                  const pctTone = pct == null
                    ? "bg-slate-100 text-slate-500"
                    : pct >= 99.9  ? "bg-emerald-100 text-emerald-700"
                    : pct >= 99    ? "bg-amber-100 text-amber-700"
                    :                "bg-rose-100 text-rose-700";
                  return (
                    <div key={b.label} className="block">
                      <a href={b.url || "#"} target="_blank" rel="noreferrer"
                         title={`Open ${b.label} uptime report — ${range.label}`}
                         data-testid={`statusbar-uptime-${b.label}`}
                         className="group block">
                        <div className="mb-1 flex items-center justify-between gap-1">
                          <span className="truncate text-[10px] font-semibold uppercase tracking-wider opacity-70 group-hover:opacity-100">
                            {b.label}
                          </span>
                          <span
                            data-testid={`statusbar-uptime-pct-${b.label}`}
                            className={`inline-flex items-center rounded-full px-1.5 py-0 text-[9px] font-semibold ${pctTone}`}
                            title={b?.avg_ms ? `avg ${b.avg_ms}ms · ${b.downtime_seconds ?? 0}s downtime` : "Pingdom"}
                          >
                            {pct == null ? "—" : `${pct.toFixed(pct >= 99.9 ? 3 : 2)}%`}
                          </span>
                        </div>
                        {b.url && (
                          <img src={bannerUrl(b.url)} alt={`${b.label} uptime — ${range.label}`} loading="lazy"
                               className="w-full rounded border border-slate-300 bg-slate-900 transition group-hover:border-slate-500" />
                        )}
                      </a>
                      {b.check_id && (
                        <UptimeSparkline checkId={b.check_id} rangeKey={rangeKey}
                                         label={b.label} />
                      )}
                    </div>
                  );
                })}
                {uptime.banners.length === 0 && (
                  <div className="col-span-full rounded border border-dashed border-slate-300 dark:border-slate-600 bg-white/40 dark:bg-slate-950/30 px-3 py-4 text-center text-[11px] text-slate-500 dark:text-slate-300"
                       data-testid="statusbar-uptime-empty">
                    {uptime.loading ? "Loading uptime data…" : "Uptime data unavailable — configure Pingdom in Settings → Dashboard."}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


// Iter 91ac — tiny sparkline strip under each Pingdom banner. Renders each
// hourly (or daily on ≥30d) bucket as a slim vertical bar tinted by the
// bucket's downtime ratio. Iter 91ad — clicking a bar opens a Popover with
// the incident details from summary.outage.
// Iter 110ag — exported so /apps/status-page can reuse the sparkline logic.
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

export const UPTIME_RANGES_EXPORT = UPTIME_RANGES;
export const LEVELS_EXPORT = LEVELS;
export function UptimeSparkline({ checkId, rangeKey, label }) {
  const [buckets, setBuckets] = useState(null);
  const [outages, setOutages] = useState([]);
  const [err, setErr] = useState(false);

  useEffect(() => {
    if (!checkId) return;
    let cancelled = false;
    setBuckets(null); setErr(false); setOutages([]);
    Promise.all([
      api.get("/status/uptime/sparkline", { params: { check_id: checkId, range: rangeKey } }),
      api.get("/status/uptime/outages",   { params: { check_id: checkId, range: rangeKey } }),
    ])
      .then(([sp, ou]) => {
        if (cancelled) return;
        if (sp.data?.error) { setErr(true); setBuckets([]); return; }
        setBuckets(sp.data.buckets || []);
        setOutages(ou.data?.outages || []);
      })
      .catch(() => { if (!cancelled) { setErr(true); setBuckets([]); } });
    return () => { cancelled = true; };
  }, [checkId, rangeKey]);

  if (buckets == null) {
    return <div className="mt-1 h-3 w-full animate-pulse rounded bg-slate-200/70" />;
  }
  if (err || buckets.length === 0) {
    return <div className="mt-1 h-3 w-full rounded bg-slate-100" title="No sparkline data" />;
  }

  const total = buckets.length;
  const bucketDuration = total > 1 ? (buckets[1].starttime - buckets[0].starttime) : 3600;
  const outagesFor = (bucket) => {
    const start = bucket.starttime;
    const end   = start + bucketDuration;
    return outages.filter((o) => o.timeto > start && o.timefrom < end);
  };

  return (
    <div className="mt-1 flex h-3 w-full items-stretch gap-[1px] overflow-hidden rounded bg-slate-100"
         data-testid={`statusbar-sparkline-${label}`}>
      {buckets.map((b, i) => {
        const known = (b.up || 0) + (b.down || 0);
        let tone;
        if (known === 0)                       tone = "bg-slate-200";       // no data
        else if ((b.down || 0) === 0)          tone = "bg-emerald-500";     // fully up
        else {
          const downPct = b.down / known;
          tone = downPct < 0.02 ? "bg-amber-400" : downPct < 0.2 ? "bg-orange-500" : "bg-rose-600";
        }
        const bucketOutages = (b.down || 0) > 0 ? outagesFor(b) : [];
        const clickable = bucketOutages.length > 0;
        const when = new Date((b.starttime || 0) * 1000).toLocaleString();
        const downMin = Math.round((b.down || 0) / 60);
        return (
          <Popover key={i}>
            <PopoverTrigger asChild disabled={!clickable}>
              <div
                style={{ flex: `1 1 ${100 / total}%` }}
                className={`${tone} ${clickable ? "cursor-pointer hover:opacity-70" : "hover:opacity-70"} transition-opacity`}
                data-testid={clickable ? `statusbar-sparkline-bar-${label}-${i}` : undefined}
                title={`${when} · ${downMin}m down · avg ${b.avg_ms}ms${clickable ? " · click for incidents" : ""}`}
              />
            </PopoverTrigger>
            {clickable && (
              <PopoverContent align="center" side="top" className="w-72 p-3"
                              data-testid={`statusbar-sparkline-popover-${label}`}>
                <SparklineIncidents label={label} bucket={b} outages={bucketOutages} />
              </PopoverContent>
            )}
          </Popover>
        );
      })}
    </div>
  );
}

function fmtDuration(sec) {
  if (sec < 60) return `${sec}s`;
  const m = Math.round(sec / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}

function SparklineIncidents({ label, bucket, outages }) {
  return (
    <div className="text-slate-800">
      <div className="mb-1.5 text-xs font-semibold text-slate-800">
        {label} · {new Date(bucket.starttime * 1000).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
      </div>
      <div className="mb-2 grid grid-cols-2 gap-2 rounded bg-slate-50 p-2 text-[11px]">
        <div>
          <div className="text-slate-400">Avg response</div>
          <div className="font-semibold tabular-nums">{bucket.avg_ms || "—"} ms</div>
        </div>
        <div>
          <div className="text-slate-400">Down in bucket</div>
          <div className="font-semibold tabular-nums text-rose-600">{fmtDuration(bucket.down || 0)}</div>
        </div>
      </div>
      <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
        Incidents ({outages.length})
      </div>
      <ol className="space-y-1">
        {outages.map((o) => (
          <li key={`${o.timefrom}-${o.timeto}`} className="flex items-baseline justify-between gap-2 text-[11px]">
            <span className="truncate text-slate-700">
              {new Date(o.timefrom * 1000).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
              {" → "}
              {new Date(o.timeto   * 1000).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
            </span>
            <span className="flex-shrink-0 rounded-full bg-rose-100 px-1.5 py-0 text-[10px] font-semibold text-rose-700">
              {fmtDuration(o.duration)}
            </span>
          </li>
        ))}
      </ol>
    </div>
  );
}

